import * as THREE from "three";
import { CARBINE, MAG_CHECK_SEC, WALK_SPEED } from "@rietz/shared";
import {
  FILL_COLOR,
  FILL_DIR,
  GLOVE_COLOR,
  HEMI_GROUND_COLOR,
  HEMI_SKY_COLOR,
  SLEEVE_COLOR,
  SUN_COLOR,
  SUN_DIR,
} from "./palette.js";
import { buildWeapon, type BuiltWeapon } from "./weaponBuilder.js";

/**
 * The first-person weapon, drawn in a SECOND render pass with its own scene,
 * camera and lights after `renderer.clearDepth()`, so it can never clip into a
 * wall. The camera never moves: the gun is authored in view space with its
 * sight axis at the origin (`weaponBuilder.ts`), so ADS is "put the group at
 * x = 0, y = 0". It does not roll with a lean — the rifle is shouldered against
 * the head that tilts; `scopeCamera` copies the main camera's quaternion so the
 * picture rolls with the world while the reticle stays put.
 *
 * All motion is procedural: idle sway (two mutually-prime sines), walk bob
 * (phase from distance travelled, like `character.ts`), recoil (a critically
 * damped spring), ADS (eased over `adsTimeSec` while the main camera eases to
 * `ADS_FOV`), reload (a timeline driven by the server's flag — one magazine
 * change, or a loop of shell inserts) and the magazine check (a shorter glance,
 * driven by `checking`).
 *
 * THE HANDS: two gloved fists with forearms, parented to the gun at
 * `BuiltWeapon.grips`, built once and re-parented per rebuild like
 * `muzzleAnchor`. The fist is an upright box so its top is exactly `HAND_H / 2`
 * above the grip point, and `attachHands` clamps any grip to `HAND_FLOOR_Y` so
 * no glove enters the sight picture (a roster test holds every model under it).
 *
 * THE SCOPE PICTURE: a magnified optic does not narrow the main camera. The
 * world is rendered again through a narrow-FOV camera at the eye into a small
 * square target shown on the lens disc (`ScopeSurface`) — picture-in-picture.
 * Driven from `WorldView.render` via `OverlayPass.preRender` and skipped
 * entirely unless magnified glass is aimed; the target is allocated on first
 * use, which keeps the class constructible under node.
 */

/**
 * Viewmodel FOV. Narrower than the world's 75, or the gun looks fish-eyed.
 * Untouched by optic zoom (the magnification lives in the scope's own render),
 * which is what makes `scopeLensFrac` closed-form.
 */
const VIEWMODEL_FOV = 55;

/**
 * Main-camera FOV while fully aimed, whatever is on the gun. Irons, a red dot
 * and an 8× all get this — narrowing the main camera to the magnification
 * threw the whole peripheral vision away; the picture lives on the glass now.
 */
export const ADS_FOV = 55;

/**
 * Floor on the scope camera's FOV — a numerical guard against a degenerate
 * projection matrix, not a design number.
 */
const MIN_SCOPE_FOV = 0.5;

/**
 * The lens disc's on-screen radius as a fraction of the half-screen height at
 * the fully-aimed pose (a constant per scope). A point `r` off-axis at `d`
 * lands at `r / d / tan(fov/2)`; `d` is `|ADS_POS.z + lensLocalZ|`.
 */
export function scopeLensFrac(radiusM: number, lensLocalZ: number): number {
  const eyeDistM = Math.abs(ADS_POS.z + lensLocalZ);
  if (!(eyeDistM > 1e-4) || !(radiusM > 0)) return 1;
  return radiusM / eyeDistM / Math.tan((VIEWMODEL_FOV * Math.PI) / 360);
}

/**
 * The scope camera FOV for an honest `zoom`×, relative to the NAKED EYE (hip
 * FOV). In tangents, not degrees (dividing degrees is 15 % off at 4×): a
 * naked-eye ray at α lands at `tan α / tan(hip/2)`; through the glass at
 * `lensFrac · tan α / tan(f/2)`, since the square target maps across the disc's
 * diameter. Hence `tan(f/2) = lensFrac · tan(hip/2) / zoom`. Drop `lensFrac`
 * and a "4×" in a quarter of the screen is really about 1×.
 */
export function scopeFovFor(hipFov: number, zoom: number, lensFrac: number): number {
  const halfRad = (hipFov * Math.PI) / 360;
  const fov = (Math.atan((lensFrac * Math.tan(halfRad)) / zoom) * 360) / Math.PI;
  return Number.isFinite(fov) && fov > MIN_SCOPE_FOV ? fov : MIN_SCOPE_FOV;
}

/**
 * Square edge of the scope's render target: ~7 % of a 1080p frame and roughly
 * 1:1 with the pixels the disc covers.
 */
const SCOPE_RT_SIZE = 512;

/** MSAA on the scope target — an aliased edge inside a scope is magnified. */
const SCOPE_RT_SAMPLES = 4;

/**
 * How far into the shoulder blend the picture switches on — partway, so the
 * swap hides inside the motion and no aimed frame shows dead glass.
 */
const SCOPE_ON_AT = 0.35;

/** Hip-fire rest pose: down and to the right, the way a rifle is carried. */
const HIP_POS = new THREE.Vector3(0.135, -0.135, -0.33);
const HIP_ROT = new THREE.Euler(0.03, -0.07, 0.04, "YXZ");

/** Aimed pose: sight axis on the camera axis. See the weapon's local contract. */
const ADS_POS = new THREE.Vector3(0, 0, -0.2);
const ADS_ROT = new THREE.Euler(0, 0, 0, "YXZ");

/** Radians of gait phase per metre travelled — matched to `character.ts`. */
const BOB_PER_METRE = 1.65;
/** Bob amplitudes at full walk speed, metres / radians. */
const BOB_POS_AMP = 0.016;
const BOB_ROT_AMP = 0.022;
/** How fast the bob amplitude follows the player's speed, per second. */
const SPEED_BLEND = 6;

/** Idle sway amplitudes, metres. Small enough to read as breathing. */
const SWAY_AMP_X = 0.0045;
const SWAY_AMP_Y = 0.0035;

/**
 * Recoil spring stiffness. ω = √k ≈ 16 rad/s: a kick is gone in ~0.25 s, inside
 * the carbine's 150 ms shot interval.
 */
const RECOIL_K = 260;
/**
 * Velocity added by one shot. Sized to read at least as violently as the real
 * aim-moving recoil in `game/aim.ts`.
 */
const RECOIL_IMPULSE = 9;
/** What one unit of spring displacement is worth, in metres / radians. */
const RECOIL_BACK_M = 0.055;
const RECOIL_UP_RAD = 0.09;
const RECOIL_ROLL_RAD = 0.05;

/** Spring integration substep, seconds. Keeps a 20 fps frame stable. */
const SPRING_STEP = 1 / 120;
const MAX_SPRING_SUBSTEPS = 8;

/**
 * Bob and sway suppression at full ADS. Exactly 1: the shot flies down the
 * camera axis and nothing here is part of it, so any aimed sway is the sights
 * lying about the point of impact. Motion still fades in with `ads`. Aimed
 * motion moved to `game/aim.ts`, which sways the CAMERA the shot is composed from.
 */
const ADS_MOTION_DAMP = 1;

/** Shared empty list, so a bare gun costs no allocation per `setWeapon` call. */
const EMPTY_MODS: readonly string[] = [];

// ── Hands ──────────────────────────────────────────────────────────────────

/** One fist. Slimmer than the third-person rig's: this one is 25 cm from the
 *  eye, and the same box at that range would be a wall. */
const HAND_W = 0.062;
const HAND_H = 0.07;
const HAND_D = 0.082;
/** The forearm behind it, running back toward the shooter's own shoulder. It
 *  leaves the frustum well before it reaches the near plane. */
const FOREARM_W = 0.058;
const FOREARM_LEN = 0.2;
/** How far each forearm swings down and outboard from its fist, radians. The
 *  fist itself stays upright: see `HAND_FLOOR_Y`. */
const FOREARM_PITCH = 0.5;
const FOREARM_YAW = 0.42;
/**
 * Minimum gap between the top of a fist and the sight axis. Sized against the
 * rear notch and the red dot's bottom bar (~1.3 cm under the axis, 15–20 cm
 * nearer the eye than the support hand).
 */
const SIGHT_CLEAR_M = 0.025;
/**
 * The highest a grip point (fist centre, weapon y) may sit and keep its fist
 * out of the sight picture. Exported for the roster test.
 */
export const HAND_FLOOR_Y = -(HAND_H / 2 + SIGHT_CLEAR_M);

// ── The held grenade (protocol v24) ────────────────────────────────────────

/**
 * Where a grenade sits while its fuse burns: low and left, mirroring the hip
 * pose, a touch nearer the eye.
 */
const GRENADE_HELD_POS = new THREE.Vector3(-0.115, -0.15, -0.3);
/** Big enough to be unmistakable at 30 cm, small enough to be a grenade. */
const GRENADE_HELD_RADIUS = 0.033;
/** Olive-black steel. The same family as the item card, deliberately. */
const GRENADE_HELD_COLOR = 0x353d2d;

// ── Reload ─────────────────────────────────────────────────────────────────

/**
 * How far into the nominal reload the swap animation runs while the server
 * still says "reloading". Short of 1: the flag clears over the network a beat
 * late, so the gun is still visibly finishing when the answer lands. The last
 * 8 % plays out when the flag clears — also what a cancelled reload gets.
 */
const RELOAD_HOLD_FRAC = 0.92;
/**
 * Where a reload cut short is fast-forwarded to: the return phase, the shortest
 * honest way out.
 */
const RELOAD_RETURN_AT = 0.65;
/** One shell into the shotgun's tube, seconds. Loops until the flag clears. */
const SHELL_SEC = 0.8;
/** Fallback reload length when nothing has told us the real one. The carbine's. */
const DEFAULT_RELOAD_SEC = 2.4;

/** Peak displacement of the gun during a magazine change. */
const RELOAD_DIP_M = 0.05;
const RELOAD_ROLL_RAD = 0.35;
const RELOAD_PITCH_RAD = 0.12;
/** The shotgun is held canted right while it is being stuffed, instead. */
const TOPUP_ROLL_RAD = 0.2;
/** How far below the fore-end the support hand drops to fetch a shell. */
const SHELL_FETCH_M = 0.22;
/** How far the dropped magazine falls before it is hidden, metres. */
const MAG_DROP_M = 0.12;

// ── The magazine check ──────────────────────────────────────────────────────
//
// A glance, not a reload: the same shape as the swap at half the amplitude over
// a third of the time, so the read is "hands busy, nothing taken apart".
// Level-driven off `YouSnap.weapon.checking`, as the reload is off `reloading`
// — the server owns the clock, so the client never starts one on its own key.

/**
 * Held short of the settle while the flag is up — `RELOAD_HOLD_FRAC`'s reason.
 */
const MAG_CHECK_HOLD_FRAC = 0.92;
/**
 * Where a check cut short is fast-forwarded to — earlier than a reload's, since
 * less is abandoned.
 */
const MAG_CHECK_RETURN_AT = 0.6;
/**
 * The pose as fractions of the reload's, same signs throughout — recognisably
 * the same hands doing less.
 */
const CHECK_ROLL_RAD = RELOAD_ROLL_RAD * 0.55;
const CHECK_PITCH_RAD = RELOAD_PITCH_RAD * 0.4;
const CHECK_DIP_M = RELOAD_DIP_M * 0.4;
/** How far the box comes out of the well, as a fraction of the full drop: far
 *  enough to see the witness holes, nowhere near far enough to be out. */
const CHECK_MAG_PULL_FRAC = 0.4;
/** The shotgun has no box to pull, so its check is a CHAMBER check: the action
 *  drawn back a finger's width to look at the shell in it. */
const CHECK_RACK_M = 0.02;

export interface ViewmodelOptions {
  /**
   * The world camera. The viewmodel owns its FOV (the shoulder blend and the
   * gun pose are one animation) and reads its pose for the scope pass.
   */
  mainCamera: THREE.PerspectiveCamera;
  /** Weapon to draw. See `buildWeapon`. */
  weaponId?: string;
}

/**
 * Everything about the equipped gun that changes how it is drawn or felt.
 * Assembled in `fireControl.syncWeapon`.
 */
export interface WeaponSetup {
  /** Aggregated ADS time, seconds. Drives the shoulder blend rate. */
  adsTimeSec: number;
  /** Fitted attachment def ids, in slot order — extra lumps on the model. */
  mods?: readonly string[];
  /**
   * Recoil kick scale: the weapon's `recoil` × its parts' `recoilMult`s. 1 is
   * the bare carbine — a bare shotgun arrives as 1.9.
   */
  recoilMult?: number;
  /** Optic magnification, or `null`/absent for iron sights. */
  zoom?: number | null;
  /**
   * Aggregated reload time, seconds — the animation covers the drum's extra
   * half second too.
   */
  reloadSec?: number;
  /**
   * True for the shotgun's shell-at-a-time top-up: a different animation (a
   * loop of inserts), not a slower one.
   */
  reloadTopup?: boolean;
}

export class Viewmodel {
  readonly scene = new THREE.Scene();
  readonly camera: THREE.PerspectiveCamera;

  /** 0 = hip, 1 = fully aimed. Read by fire control to scale hip spread. */
  adsT = 0;

  private readonly mainCamera: THREE.PerspectiveCamera;
  private readonly hipFov: number;

  private readonly rig = new THREE.Group();
  private weapon: BuiltWeapon;
  /** Item def id of the gun currently built. */
  private weaponId: string;
  /**
   * The fitted parts the current model was built with, joined — a suppressor
   * changes the model without changing `weaponId`.
   */
  private modsKey = "";
  /** The equipped weapon's ADS time; the blend rate is per-gun from Phase 4. */
  private adsTimeSec: number;
  /** Weapon kick × fitted parts — the shove a muzzle brake takes the edge off. */
  private recoilMult = 1;
  /** Fitted optic magnification, or `null` for irons. Decides the scope FOV. */
  private zoom: number | null = null;
  /**
   * A stable muzzle anchor, re-parented onto each new weapon's muzzle, so
   * `Fx`'s flash survives a weapon swap.
   */
  private readonly muzzleAnchor = new THREE.Object3D();
  /**
   * The two hands, built once and re-parented onto each new gun — the same
   * bargain `muzzleAnchor` makes.
   */
  private readonly handTrigger: THREE.Group;
  private readonly handSupport: THREE.Group;
  /** Rest points of the two hands and the magazine, in the CURRENT gun's local
   *  space. Re-read on every rebuild; the reload animation lerps between them. */
  private readonly gripTrigger = new THREE.Vector3();
  private readonly gripSupport = new THREE.Vector3();
  private readonly gripMag = new THREE.Vector3();
  /**
   * The current gun's magazine component and seated height, or `null` (shotgun,
   * pistol, or an empty well). See `BuiltWeapon.magMesh`.
   */
  private magMesh: THREE.Object3D | null = null;
  private magRestY = 0;

  /** What the server last said. `reloadT` is what is actually being drawn. */
  private reloadHeld = false;
  /** Seconds into the reload animation; 0 means "nothing playing". */
  private reloadT = 0;
  /** Where the play-out ends once the flag has cleared. See `setReload`. */
  private reloadEnd = 0;
  /** The equipped gun's reload length and style, from `WeaponSetup`. */
  private reloadDurSec = DEFAULT_RELOAD_SEC;
  private reloadTopup = false;

  /**
   * The magazine check's twin of the three above. Its duration is
   * `MAG_CHECK_SEC`, the server's number. See `setMagCheck`.
   */
  private checkHeld = false;
  private checkT = 0;
  private checkEnd = 0;

  /**
   * The grenade in the off hand while the pin is out. Parented to the SCENE,
   * not the rig, so it does not move with the gun — the read is "one hand has
   * left the weapon". The weapon is not lowered: cooking while covering a
   * doorway is a real thing to do.
   */
  private readonly grenadeHeld: THREE.Mesh;

  private readonly lights: THREE.Object3D[] = [];
  private readonly disposables: { dispose(): void }[] = [];

  /** Gait phase, radians; accumulated from distance travelled. */
  private phase = 0;
  /** Seconds since construction, for the idle sway. */
  private clock = 0;
  /** Smoothed horizontal speed, m/s. */
  private speed = 0;

  /** Recoil spring displacement and its velocity. */
  private recoil = 0;
  private recoilVel = 0;
  /** Which way this shot's roll went — re-rolled per trigger pull. */
  private rollSign = 1;

  /** Last FOV pushed to the main camera, so we only touch the matrix on change. */
  private lastFov: number;

  /**
   * The camera the scope picture is shot with, posed from the MAIN camera (the
   * round flies down the camera axis, so the scope must look there too). Aspect
   * 1 for the square target; renders the world's scene.
   */
  private readonly scopeCamera = new THREE.PerspectiveCamera(30, 1, 0.05, 900);
  /**
   * Created on first use in `preRender`: a player who never fits glass never
   * pays for it, and it keeps the class inert under node.
   */
  private scopeRt: THREE.WebGLRenderTarget | null = null;
  /** The live material — the target's texture on the lens. Lazy, with the RT. */
  private scopeMat: THREE.MeshBasicMaterial | null = null;
  /**
   * The current gun's ocular lens and its idle dark-glass material, or `null`
   * without magnifying glass. Re-derived on every rebuild.
   */
  private scopeLens: THREE.Mesh | null = null;
  private scopeIdleMat: THREE.Material | THREE.Material[] | null = null;
  /** Whether the lens currently wears the live material. Drives `preRender`. */
  private scopeOn = false;

  /** Scratch — `update` runs every frame and must not allocate. */
  private readonly tmpPos = new THREE.Vector3();
  private readonly tmpRot = new THREE.Euler(0, 0, 0, "YXZ");

  constructor(opts: ViewmodelOptions) {
    this.mainCamera = opts.mainCamera;
    this.hipFov = opts.mainCamera.fov;
    this.lastFov = opts.mainCamera.fov;

    this.scene.name = "viewmodel";
    // No fog: the gun is 30 cm from the eye and haze on it would be a bug.
    this.camera = new THREE.PerspectiveCamera(VIEWMODEL_FOV, 1, 0.01, 12);
    this.camera.rotation.order = "YXZ";

    // The same three-light setup the world uses (`render/sky.ts`), at the same
    // directions, so the gun is lit by the same sun as everything behind it.
    const hemi = new THREE.HemisphereLight(HEMI_SKY_COLOR, HEMI_GROUND_COLOR, 1.5);
    const sun = new THREE.DirectionalLight(SUN_COLOR, 1.5);
    sun.position.set(SUN_DIR.x, SUN_DIR.y, SUN_DIR.z);
    const fill = new THREE.DirectionalLight(FILL_COLOR, 0.5);
    fill.position.set(FILL_DIR.x, FILL_DIR.y, FILL_DIR.z);
    for (const l of [hemi, sun, fill]) {
      this.scene.add(l);
      this.lights.push(l);
      this.disposables.push(l);
    }

    // Mirrored, so both forearms lean toward the shoulder they belong to. The
    // fists themselves are unrotated boxes.
    this.handTrigger = makeHand(this.disposables, +FOREARM_YAW);
    this.handSupport = makeHand(this.disposables, -FOREARM_YAW);

    this.weaponId = opts.weaponId ?? CARBINE.id;
    this.adsTimeSec = CARBINE.adsTimeSec;
    // Factory view: no parts list yet, so the placeholder is a whole gun.
    this.weapon = buildWeapon(this.weaponId);
    this.weapon.muzzle.add(this.muzzleAnchor);
    this.attachHands();
    this.rig.add(this.weapon.group);
    this.rig.rotation.order = "YXZ";
    this.rig.position.copy(HIP_POS);
    this.rig.rotation.copy(HIP_ROT);
    this.scene.add(this.rig);

    // Built once, like everything here — arming must not compile a material.
    const grenadeGeo = new THREE.SphereGeometry(GRENADE_HELD_RADIUS, 10, 8);
    const grenadeMat = new THREE.MeshLambertMaterial({
      color: GRENADE_HELD_COLOR,
      flatShading: true,
    });
    this.disposables.push(grenadeGeo, grenadeMat);
    this.grenadeHeld = new THREE.Mesh(grenadeGeo, grenadeMat);
    this.grenadeHeld.position.copy(GRENADE_HELD_POS);
    this.grenadeHeld.visible = false;
    this.scene.add(this.grenadeHeld);

    this.scopeCamera.rotation.order = "YXZ";
    this.syncScope();
  }

  /** Shows or hides the grenade in the off hand. Cheap enough to call every
   *  frame; the game side does exactly that. */
  setGrenadeHeld(held: boolean): void {
    this.grenadeHeld.visible = held;
  }

  /** Where a muzzle flash belongs. Lives in THIS scene, not the world's. */
  get muzzle(): THREE.Object3D {
    return this.muzzleAnchor;
  }

  /**
   * The barrel tip in the MAIN camera's view space this frame, sway and recoil
   * included. Allocation-free. The viewmodel camera is at the origin, so the
   * anchor's world position is already view space — except this scene is shot
   * at `VIEWMODEL_FOV` and the world at the blended main FOV, so x/y are scaled
   * by the ratio of the half-angle tangents to land on the same pixel (this is
   * what lets the laser in `game/tacticalFx.ts` start where the gun ends).
   */
  muzzleInView(out: THREE.Vector3): THREE.Vector3 {
    // `getWorldPosition` refreshes the ancestor chain, so this is current even
    // though the scene has not been rendered since `update` posed the rig.
    this.muzzleAnchor.getWorldPosition(out);
    const k =
      Math.tan((VIEWMODEL_FOV * Math.PI) / 360) /
      Math.tan((this.mainCamera.fov * Math.PI) / 360);
    out.x *= k;
    out.y *= k;
    return out;
  }

  /**
   * Whether the scope picture is currently rendered. Read only by the smoke
   * test and debug HUD.
   */
  get scopeActive(): boolean {
    return this.scopeOn;
  }

  /** The FOV the scope picture is being rendered at, degrees. See above. */
  get scopeFov(): number {
    return this.scopeCamera.fov;
  }

  /**
   * Swaps the gun in the player's hands. `id` is a def id, or `null` for
   * empty-handed (the rig is hidden, not torn down). The model is rebuilt when
   * the gun or its parts change; everything else in `setup` is a number that
   * takes effect next frame. A no-op when nothing changed, so it is safe per frame.
   */
  setWeapon(id: string | null, setup: WeaponSetup): void {
    this.adsTimeSec = setup.adsTimeSec > 0 ? setup.adsTimeSec : CARBINE.adsTimeSec;
    this.recoilMult = setup.recoilMult !== undefined && setup.recoilMult > 0 ? setup.recoilMult : 1;
    this.reloadDurSec =
      setup.reloadSec !== undefined && setup.reloadSec > 0 ? setup.reloadSec : DEFAULT_RELOAD_SEC;
    this.reloadTopup = setup.reloadTopup === true;
    const zoom = setup.zoom ?? null;
    const zoomChanged = zoom !== this.zoom;
    this.zoom = zoom;

    if (id === null) {
      // Not torn down, so the lens references stay valid; the hidden rig is what
      // `update` sees, and it takes the picture down on the next frame.
      this.rig.visible = false;
      return;
    }
    this.rig.visible = true;

    const mods = setup.mods ?? EMPTY_MODS;
    const modsKey = mods.join(",");
    if (id === this.weaponId && modsKey === this.modsKey) {
      // Same model, but a variable zoom or a swap between two guns carrying the
      // same parts can still change the magnification the picture is shot at.
      if (zoomChanged) this.syncScope();
      return;
    }
    this.weaponId = id;
    this.modsKey = modsKey;

    // The anchor survives; it must be re-parented onto the NEW muzzle, since a
    // suppressor moves the tip 19 cm forward.
    this.muzzleAnchor.removeFromParent();
    this.handTrigger.removeFromParent();
    this.handSupport.removeFromParent();
    this.rig.remove(this.weapon.group);
    this.weapon.dispose();

    // `"missing"` view: `setup.mods` is the authoritative deep parts list, so
    // an
    // empty slot draws nothing.
    this.weapon = buildWeapon(id, mods, { bare: "missing" });
    this.weapon.muzzle.add(this.muzzleAnchor);
    // A reload cannot survive the gun it played on; the flag is re-asserted
    // next snapshot if the server still has one running.
    this.reloadHeld = false;
    this.reloadT = 0;
    // Nor can a magazine check, on the same contract.
    this.checkHeld = false;
    this.checkT = 0;
    this.attachHands();
    this.rig.add(this.weapon.group);
    // The old lens is already disposed; the live material is ours and outlives
    // it.
    this.syncScope();
  }

  /**
   * Whether the player is reloading, off `YouSnap.weapon.reloading`. A level,
   * not an event: starts on the rising edge, holds short of the settle while
   * up (`RELOAD_HOLD_FRAC`), plays out when it drops. Safe to call every frame.
   */
  setReload(active: boolean): void {
    if (active === this.reloadHeld) return;
    this.reloadHeld = active;
    if (active) {
      this.reloadT = 0;
      return;
    }
    if (this.reloadT <= 0) return;
    if (this.reloadTopup) {
      // Finish the shell that is in the hand, and stop at the cycle boundary —
      // which is where the animation is already back at the rest pose.
      this.reloadEnd = Math.ceil(this.reloadT / SHELL_SEC) * SHELL_SEC;
      return;
    }
    this.reloadEnd = this.reloadDurSec;
    this.reloadT = Math.max(this.reloadT, this.reloadDurSec * RELOAD_RETURN_AT);
  }

  /**
   * Whether the player is looking into the magazine, off
   * `YouSnap.weapon.checking`. The same contract as `setReload`; no topup
   * variant.
   */
  setMagCheck(active: boolean): void {
    if (active === this.checkHeld) return;
    this.checkHeld = active;
    if (active) {
      this.checkT = 0;
      return;
    }
    if (this.checkT <= 0) return;
    this.checkEnd = MAG_CHECK_SEC;
    this.checkT = Math.max(this.checkT, MAG_CHECK_SEC * MAG_CHECK_RETURN_AT);
  }

  /** Matches the main camera's aspect; called from `WorldView.resize`. */
  setAspect(aspect: number): void {
    this.camera.aspect = aspect;
    this.camera.updateProjectionMatrix();
  }

  /**
   * One trigger pull: kick the spring and re-roll the roll direction. Scaled by
   * the fitted parts.
   */
  kick(): void {
    this.recoilVel += RECOIL_IMPULSE * this.recoilMult;
    // Math.random is fine here — recoil VISUALS are client-side only; the
    // direction actually sent to the server is decided in `fireControl.ts`.
    this.rollSign = Math.random() < 0.5 ? -1 : 1;
  }

  /**
   * One frame. `movedM` is the distance the rendered player travelled — the
   * same value the camera moved by.
   */
  update(dtMs: number, movedM: number, adsHeld: boolean): void {
    const dt = Math.min(Math.max(dtMs, 0), 100) / 1000;
    this.clock += dt;

    // --- Reload clock ------------------------------------------------------
    // Before the ADS blend, because it is allowed to override it.
    this.stepReloadClock(dt);
    // ...and the magazine check's, which is allowed to override the blend too.
    this.stepCheckClock(dt);

    // --- ADS blend: symmetric. A reload (and a magazine check) forces the gun
    // down whatever the button says — the animation happens under the sight
    // axis and would be invisible behind a scope.
    const adsRate = dt / Math.max(this.adsTimeSec, 1e-3);
    const adsTarget = adsHeld && this.reloadT <= 0 && this.checkT <= 0 ? 1 : 0;
    if (this.adsT < adsTarget) this.adsT = Math.min(adsTarget, this.adsT + adsRate);
    else if (this.adsT > adsTarget) this.adsT = Math.max(adsTarget, this.adsT - adsRate);
    const ads = smoothstep(this.adsT);

    // --- Speed and gait phase ----------------------------------------------
    // A teleport (respawn, hard reconcile snap) must not spin the gun.
    const step = movedM > 2 ? 0 : movedM;
    const instant = dt > 0 ? step / dt : 0;
    this.speed += (instant - this.speed) * Math.min(1, dt * SPEED_BLEND);
    this.phase += step * BOB_PER_METRE;
    if (this.phase > Math.PI * 2) this.phase -= Math.PI * 2;

    const gait = Math.min(this.speed / WALK_SPEED, 1.4);
    const motion = 1 - ADS_MOTION_DAMP * ads;

    // --- Recoil spring ------------------------------------------------------
    this.stepRecoil(dt);

    // --- Compose the pose ---------------------------------------------------
    const pos = this.tmpPos;
    const rot = this.tmpRot;
    pos.copy(HIP_POS).lerp(ADS_POS, ads);
    rot.x = HIP_ROT.x + (ADS_ROT.x - HIP_ROT.x) * ads;
    rot.y = HIP_ROT.y + (ADS_ROT.y - HIP_ROT.y) * ads;
    rot.z = HIP_ROT.z + (ADS_ROT.z - HIP_ROT.z) * ads;

    // Idle sway: two slow sines at unrelated rates, so the loop never reads as
    // a loop.
    pos.x += Math.sin(this.clock * 0.61) * SWAY_AMP_X * motion;
    pos.y += Math.sin(this.clock * 0.87) * SWAY_AMP_Y * motion;

    // Walk bob: lateral at the gait frequency, vertical at twice it (one dip
    // per footfall, two per stride).
    const bob = gait * motion;
    pos.x += Math.sin(this.phase) * BOB_POS_AMP * bob;
    pos.y += Math.abs(Math.cos(this.phase)) * BOB_POS_AMP * 0.8 * bob;
    rot.z += Math.sin(this.phase) * BOB_ROT_AMP * bob;

    // Recoil: back, muzzle up, a little roll. Rotation is gone at full ADS —
    // the real kick lives in `game/aim.ts` and moves the camera; a gun rocking
    // in an aimed view puts the bead where the round is not. The straight-back
    // shove survives because a translation along the sight axis cannot lie.
    const recoilRot = this.recoil * (1 - ads);
    pos.z += this.recoil * RECOIL_BACK_M;
    rot.x += recoilRot * RECOIL_UP_RAD;
    rot.z += recoilRot * RECOIL_ROLL_RAD * this.rollSign;

    // Reload: the gun's own displacement, on top of everything above.
    this.applyReload(pos, rot);
    // After the reload: its idle branch restores the support hand and magazine
    // mesh every frame, so the check must be the last writer.
    this.applyMagCheck(pos, rot);

    this.rig.position.copy(pos);
    this.rig.rotation.set(rot.x, rot.y, rot.z);

    // --- Main-camera shoulder FOV, interpolated from the hip FOV over the same
    // blend. The optic is NOT in this expression — what magnifies is the glass.
    this.applyFov(this.hipFov + (ADS_FOV - this.hipFov) * ads);

    // --- The scope picture: a material SWAP (a pointer write) rather than
    // reassigning `map`, which can rebuild the program on the shouldering
    // frame.
    // `scopeLens` is non-null only with magnifying glass (see `syncScope`).
    const lens = this.scopeLens;
    const wantScope = lens !== null && this.rig.visible && this.adsT > SCOPE_ON_AT;
    if (wantScope !== this.scopeOn) {
      this.scopeOn = wantScope;
      if (lens !== null) {
        // The live material may not exist yet on the first activation — it is
        // born with the render target in `preRender`.
        const next = wantScope ? this.scopeMat : this.scopeIdleMat;
        if (next !== null) lens.material = next;
      }
    }
  }

  /**
   * The scope's picture-in-picture pass, called by `WorldView.render` before
   * the world is drawn. Returns immediately unless magnified glass is aimed.
   * Allocation-free after the lazy init. Costs one more pass over the world at
   * 512² (~7 % of a 1080p frame's fragments, a second run of the draw calls,
   * and with `?shadows=1` a second shadow-map render).
   */
  preRender(renderer: THREE.WebGLRenderer, worldScene: THREE.Scene): void {
    if (!this.scopeOn) return;
    const lens = this.scopeLens;
    if (lens === null) return;

    let rt = this.scopeRt;
    if (rt === null) {
      rt = new THREE.WebGLRenderTarget(SCOPE_RT_SIZE, SCOPE_RT_SIZE, {
        samples: SCOPE_RT_SAMPLES,
      });
      this.scopeRt = rt;
    }
    let mat = this.scopeMat;
    if (mat === null) {
      mat = new THREE.MeshBasicMaterial({ map: rt.texture });
      this.scopeMat = mat;
    }
    // First activation, or a rebuild handed us a fresh dark lens mid-picture.
    if (lens.material !== mat) lens.material = mat;

    // From the exact eye the main pass uses; a frame of lag reads as the
    // picture sliding against the housing.
    this.scopeCamera.position.copy(this.mainCamera.position);
    this.scopeCamera.quaternion.copy(this.mainCamera.quaternion);

    // `autoClear` is still true here, so the target clears itself.
    renderer.setRenderTarget(rt);
    renderer.render(worldScene, this.scopeCamera);
    renderer.setRenderTarget(null);
  }

  dispose(): void {
    this.applyFov(this.hipFov);
    if (this.scopeLens !== null && this.scopeIdleMat !== null) {
      this.scopeLens.material = this.scopeIdleMat;
    }
    this.scopeLens = null;
    this.scopeIdleMat = null;
    this.scopeOn = false;
    // The target owns its texture; the material only borrowed it.
    this.scopeRt?.dispose();
    this.scopeRt = null;
    this.scopeMat?.dispose();
    this.scopeMat = null;
    this.muzzleAnchor.removeFromParent();
    this.handTrigger.removeFromParent();
    this.handSupport.removeFromParent();
    this.magMesh = null;
    this.rig.remove(this.weapon.group);
    this.weapon.dispose();
    for (const l of this.lights) this.scene.remove(l);
    for (const d of this.disposables) d.dispose();
    this.lights.length = 0;
    this.disposables.length = 0;
    this.scene.clear();
  }

  // -----------------------------------------------------------------------

  /**
   * Re-reads the hold from the current model and hangs the hands off it.
   * Rebuild only — nothing here changes per frame.
   */
  private attachHands(): void {
    const w = this.weapon;
    // The sight-axis floor. A grip the model put too high is pushed down onto
    // it rather than trusted — see `HAND_FLOOR_Y`.
    this.gripTrigger.copy(w.grips.trigger);
    this.gripTrigger.y = Math.min(this.gripTrigger.y, HAND_FLOOR_Y);
    this.gripSupport.copy(w.grips.support);
    this.gripSupport.y = Math.min(this.gripSupport.y, HAND_FLOOR_Y);
    // No gun in the roster lacks a mag anchor, but the map is partial; the
    // origin would put the hand on the sight axis, so fall back to the support
    // grip.
    this.gripMag.copy(w.modAnchors.mag ?? w.grips.support);

    this.handTrigger.position.copy(this.gripTrigger);
    this.handSupport.position.copy(this.gripSupport);
    w.group.add(this.handTrigger);
    w.group.add(this.handSupport);

    // The old mesh died with the old build; a stale reference here would be a
    // write into a disposed object, exactly like `scopeLens`.
    this.magMesh = w.magMesh ?? null;
    this.magRestY = this.magMesh === null ? 0 : this.magMesh.position.y;
  }

  /**
   * Advances the reload timeline by one frame. See `setReload`. One comparison
   * when idle.
   */
  private stepReloadClock(dt: number): void {
    if (!this.reloadHeld && this.reloadT <= 0) return;
    this.reloadT += dt;
    if (this.reloadHeld) {
      // The top-up loops for as long as the flag is up: how many shells go in is
      // the server's business and the animation is not counting them.
      if (this.reloadTopup) return;
      const hold = this.reloadDurSec * RELOAD_HOLD_FRAC;
      if (this.reloadT > hold) this.reloadT = hold;
      return;
    }
    if (this.reloadT >= this.reloadEnd) this.reloadT = 0;
  }

  /**
   * Advances the magazine-check timeline. `stepReloadClock` without the loop
   * branch — a glance is one pass.
   */
  private stepCheckClock(dt: number): void {
    if (!this.checkHeld && this.checkT <= 0) return;
    this.checkT += dt;
    if (this.checkHeld) {
      const hold = MAG_CHECK_SEC * MAG_CHECK_HOLD_FRAC;
      if (this.checkT > hold) this.checkT = hold;
      return;
    }
    if (this.checkT >= this.checkEnd) this.checkT = 0;
  }

  /**
   * Writes the reload's contribution into this frame's pose and moves the
   * support hand (and the magazine on a swap). `swap`: one pass — dip, roll,
   * pitch up, magazine drops, hand to the well and back. `topup`: a loop of
   * `SHELL_SEC` inserts with the gun canted. Both are 0 at t = 0 and at the end
   * of a cycle, so `stepReloadClock` can stop by zeroing the clock.
   */
  private applyReload(pos: THREE.Vector3, rot: THREE.Euler): void {
    const mag = this.magMesh;
    if (this.reloadT <= 0) {
      // Idle: put back anything a previous cycle moved, and only then — an
      // animation that ended is not one that has to be re-ended every frame.
      if (!this.handSupport.position.equals(this.gripSupport)) {
        this.handSupport.position.copy(this.gripSupport);
      }
      if (mag !== null && (!mag.visible || mag.position.y !== this.magRestY)) {
        mag.visible = true;
        mag.position.y = this.magRestY;
      }
      return;
    }

    if (this.reloadTopup) {
      const c = (this.reloadT % SHELL_SEC) / SHELL_SEC;
      // Cant ramped in and out over a quarter-second; the loop itself must not
      // wobble the gun.
      const on = this.reloadHeld
        ? Math.min(1, this.reloadT / 0.25)
        : clamp01((this.reloadEnd - this.reloadT) / 0.25);
      rot.z += TOPUP_ROLL_RAD * on;
      pos.y -= RELOAD_DIP_M * 0.5 * on;

      // First half: down out of frame for a shell. Second half: up to the port.
      const fetch = Math.sin(Math.PI * Math.min(1, c / 0.5));
      const port =
        c < 0.5
          ? 0
          : smoothstep(clamp01((c - 0.5) / 0.25)) *
            (1 - smoothstep(clamp01((c - 0.75) / 0.25)));
      this.handSupport.position.lerpVectors(this.gripSupport, this.gripMag, port);
      this.handSupport.position.y -= SHELL_FETCH_M * fetch;
      return;
    }

    const t = Math.min(1, this.reloadT / Math.max(this.reloadDurSec, 1e-3));
    const dip = swapDipAt(t);
    pos.y -= RELOAD_DIP_M * dip;
    rot.z += RELOAD_ROLL_RAD * dip;
    rot.x += RELOAD_PITCH_RAD * dip;

    // The hand: down to the well, a shove to seat the fresh box, back up.
    const reach = swapReachAt(t);
    this.handSupport.position.lerpVectors(this.gripSupport, this.gripMag, reach);
    this.handSupport.position.y += MAG_DROP_M * insertBumpAt(t);

    if (mag === null) return;
    // The old magazine falls out under the dip and is gone by the time the hand
    // is at the well; the new one rides up with the seating shove.
    if (t < 0.3) {
      mag.visible = true;
      mag.position.y = this.magRestY - MAG_DROP_M * smoothstep(clamp01(t / 0.3));
    } else if (t < 0.5) {
      mag.visible = false;
    } else {
      mag.visible = true;
      mag.position.y = this.magRestY - MAG_DROP_M * (1 - smoothstep(clamp01((t - 0.5) / 0.18)));
    }
  }

  /**
   * Writes the magazine check's contribution into this frame's pose: off the
   * shoulder, well rolled toward the eye, the box part-way out, a look, a bump
   * home. Shorter and shallower than a reload so the two read differently.
   *
   * No idle branch — `applyReload` restores hand and magazine every frame it
   * does nothing, and `update` calls it first. Does nothing while a reload is
   * playing: the server never overlaps the two but the client's play-out tails
   * can, and two writers of one hand would put it in neither place. Two
   * flavours: the magazine check (box moves) and the shotgun's chamber check
   * (action comes back a finger's width).
   */
  private applyMagCheck(pos: THREE.Vector3, rot: THREE.Euler): void {
    if (this.checkT <= 0 || this.reloadT > 0) return;

    const t = Math.min(1, this.checkT / MAG_CHECK_SEC);
    const a = checkEnvelopeAt(t);
    // The envelope's settle overshoot belongs to the gun, not the hand or box
    // (a magazine pushed INTO the receiver would be wrong).
    const reach = clamp01(a);

    if (this.reloadTopup) {
      // Canted the way the top-up cants it — right, not inboard — so the two
      // things this gun does with its hands read as the same gun doing them.
      rot.z += TOPUP_ROLL_RAD * 0.8 * a;
      rot.x += CHECK_PITCH_RAD * a;
      pos.y -= CHECK_DIP_M * 0.5 * a;
      // Hand to the port; the action comes back toward the shoulder (+z).
      this.handSupport.position.lerpVectors(this.gripSupport, this.gripMag, reach);
      this.handSupport.position.z += CHECK_RACK_M * reach;
      return;
    }

    rot.z += CHECK_ROLL_RAD * a;
    rot.x += CHECK_PITCH_RAD * a;
    pos.y -= CHECK_DIP_M * a;

    // The hand off the fore-end and onto the box, plus the short shove that
    // seats it again on the way back.
    this.handSupport.position.lerpVectors(this.gripSupport, this.gripMag, reach);
    this.handSupport.position.y += MAG_DROP_M * checkSeatBumpAt(t);

    const mag = this.magMesh;
    if (mag === null) return;
    // Visible throughout — unlike the reload, nothing leaves the gun.
    mag.visible = true;
    mag.position.y = this.magRestY - MAG_DROP_M * CHECK_MAG_PULL_FRAC * reach;
  }

  /**
   * Critically damped spring (c = 2√k), substepped so a 20 fps frame stays
   * stable.
   */
  private stepRecoil(dt: number): void {
    if (this.recoil === 0 && this.recoilVel === 0) return;

    const c = 2 * Math.sqrt(RECOIL_K);
    let remaining = dt;
    for (let i = 0; i < MAX_SPRING_SUBSTEPS && remaining > 0; i++) {
      const h = Math.min(remaining, SPRING_STEP);
      remaining -= h;
      this.recoilVel += (-RECOIL_K * this.recoil - c * this.recoilVel) * h;
      this.recoil += this.recoilVel * h;
    }

    // Settle exactly, so an idle gun costs nothing next frame.
    if (Math.abs(this.recoil) < 1e-4 && Math.abs(this.recoilVel) < 1e-3) {
      this.recoil = 0;
      this.recoilVel = 0;
    }
  }

  /**
   * Re-derives the scope picture from the current model and zoom: which lens,
   * its idle material, and the second pass's FOV. Called on rebuild and zoom
   * change only. `scopeLens` stays `null` for irons, a red dot and a 1× optic
   * (a 1× picture is a frame's GPU spent on nothing).
   */
  private syncScope(): void {
    // A lens surviving a zoom-only resync may still wear the live picture —
    // give it its own glass back FIRST, or the capture below would record the
    // live material as idle and freeze the last frame on it.
    if (this.scopeLens !== null && this.scopeIdleMat !== null && this.scopeMat !== null) {
      if (this.scopeLens.material === this.scopeMat) {
        this.scopeLens.material = this.scopeIdleMat;
      }
    }
    this.scopeOn = false;
    this.scopeLens = null;
    this.scopeIdleMat = null;

    const scope = this.weapon.scope;
    const zoom = this.zoom;
    if (scope === undefined || zoom === null || !(zoom > 1)) return;

    this.scopeLens = scope.lens;
    this.scopeIdleMat = scope.lens.material;
    this.scopeCamera.fov = scopeFovFor(
      this.hipFov,
      zoom,
      scopeLensFrac(scope.radiusM, scope.lens.position.z),
    );
    this.scopeCamera.updateProjectionMatrix();
  }

  /** Writes the main camera's FOV, skipping the matrix rebuild when unchanged. */
  private applyFov(fov: number): void {
    if (Math.abs(fov - this.lastFov) < 1e-3) return;
    this.lastFov = fov;
    this.mainCamera.fov = fov;
    this.mainCamera.updateProjectionMatrix();
  }
}

/** Smoothstep on [0,1] — eases both ends of the ADS transition. */
function smoothstep(t: number): number {
  return t * t * (3 - 2 * t);
}

function clamp01(t: number): number {
  return t < 0 ? 0 : t > 1 ? 1 : t;
}

/**
 * One gloved fist and forearm, as a group whose origin is the fist. The fist
 * stays upright and only the forearm is angled at the wrist — keeping rotation
 * off the fist is what makes its top exactly `HAND_H / 2` above the grip point
 * (`HAND_FLOOR_Y` depends on it). Materials go on the viewmodel's disposables.
 */
function makeHand(disposables: { dispose(): void }[], yaw: number): THREE.Group {
  const group = new THREE.Group();

  const gloveMat = new THREE.MeshLambertMaterial({ color: GLOVE_COLOR, flatShading: true });
  const sleeveMat = new THREE.MeshLambertMaterial({ color: SLEEVE_COLOR, flatShading: true });
  disposables.push(gloveMat, sleeveMat);

  const fist = new THREE.Mesh(new THREE.BoxGeometry(HAND_W, HAND_H, HAND_D), gloveMat);
  group.add(fist);

  // The wrist on the fist's rear face (+z), angled down and out.
  const wrist = new THREE.Group();
  wrist.position.z = HAND_D * 0.5;
  wrist.rotation.order = "YXZ";
  wrist.rotation.set(FOREARM_PITCH, yaw, 0);
  group.add(wrist);
  const arm = new THREE.Mesh(
    new THREE.BoxGeometry(FOREARM_W, FOREARM_W, FOREARM_LEN),
    sleeveMat,
  );
  arm.position.z = FOREARM_LEN * 0.5;
  wrist.add(arm);

  disposables.push(fist.geometry, arm.geometry);
  return group;
}

/**
 * How far the gun is out of its firing pose over one magazine change, 0 → 1 → 0.
 * Settles through a small negative — pulled past rest into the shoulder — or
 * the return reads as switched off rather than finished.
 */
function swapDipAt(t: number): number {
  if (t < 0.35) return smoothstep(clamp01(t / 0.35));
  if (t < 0.65) return 1;
  const u = clamp01((t - 0.65) / 0.35);
  return 1 - smoothstep(u) - Math.sin(Math.PI * u) * 0.12;
}

/** How far the support hand is from its grip toward the magazine well. */
function swapReachAt(t: number): number {
  if (t < 0.3) return smoothstep(clamp01(t / 0.3));
  if (t < 0.62) return 1;
  return 1 - smoothstep(clamp01((t - 0.62) / 0.28));
}

/** The short shove that seats the fresh magazine, around the middle of the
 *  cycle: the hand comes up off the well and pushes the box home with it. */
function insertBumpAt(t: number): number {
  if (t < 0.42 || t > 0.62) return 0;
  return Math.sin(Math.PI * ((t - 0.42) / 0.2)) * 0.35;
}

/**
 * `swapDipAt` tightened for the check: in over 30 %, held to 65 %, out with the
 * same small negative. One envelope drives roll, pitch, dip, hand and box.
 */
function checkEnvelopeAt(t: number): number {
  if (t < 0.3) return smoothstep(clamp01(t / 0.3));
  if (t < 0.65) return 1;
  const u = clamp01((t - 0.65) / 0.35);
  return 1 - smoothstep(u) - Math.sin(Math.PI * u) * 0.12;
}

/** `insertBumpAt`'s little brother, later in the cycle and half the size: the
 *  box was never out, so pushing it back in is a nudge rather than a shove. */
function checkSeatBumpAt(t: number): number {
  if (t < 0.68 || t > 0.85) return 0;
  return Math.sin(Math.PI * ((t - 0.68) / 0.17)) * 0.16;
}
